import React, { useState, useEffect } from "react";
import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet";
import L from "leaflet";
import { MessageSquare, Star, X } from "lucide-react";
import useTasks from "../../../Sevices/useTasks";

// --------- MAP ICONS ----------
const iconRed = new L.Icon({
  iconUrl:
    "https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-red.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
});

const iconYellow = new L.Icon({
  iconUrl:
    "https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-yellow.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
});

const iconDriverActive = new L.Icon({
  iconUrl: "http://bumppy.in/packky/api/v1/uploads/images/driver_icon.png",
  iconSize: [30, 30],
  iconAnchor: [15, 30],
  popupAnchor: [0, -25],
});

// -------- STATUS OPTIONS ----------
const statusOptions = [
  "unassigned",
  "assigned",
  "acknowledged",
  "arrived",
  "food is ready",
  "picked up",
  "completed",
  "successful",
  "cancelled",
];

export default function TaskDetailsModal({
  show,
  task,
  onClose,
  isLoading,
  assignmentLogs = [],
  statusChangeLogs = [],
  // showLogs,
  // setShowLogs,
  showLogs = false,
setShowLogs = () => {},
  onStatusChange,
  onFetchLogs,
  onUnassignRequest,
}) {
  const getLatestStatus = () => {
    if (statusChangeLogs?.length > 0) {
      return (
        statusChangeLogs[statusChangeLogs.length - 1]?.status ||
        task?.status ||
        ""
      ).toLowerCase();
    }
    return (task?.status || "").toLowerCase();
  };

  const { fetchTaskDetails } = useTasks();

  const [detailTask, setDetailTask] = useState(null);
  const [detailsLoading, setDetailsLoading] = useState(false);

  const [selectedStatus, setSelectedStatus] = useState(getLatestStatus());

  const taskId = task?.pk_task_id || task?.taskId || task?.id;
  const modalTask = detailTask || task;

  useEffect(() => {
    setSelectedStatus(
      (
        statusChangeLogs?.[statusChangeLogs.length - 1]?.status ||
        modalTask?.status ||
        ""
      ).toLowerCase(),
    );
  }, [modalTask?.status, statusChangeLogs]);

  useEffect(() => {
    const loadTaskDetails = async () => {
      if (!show || !taskId) return;

      setDetailTask(null);
      setDetailsLoading(true);

      try {
        const taskDetails = await fetchTaskDetails(taskId);

        if (taskDetails) {
          const finalTask = Array.isArray(taskDetails)
            ? taskDetails[0]
            : taskDetails;

          setDetailTask(finalTask);
        } else {
          setDetailTask(task);
        }
      } catch (error) {
        console.error("Error loading task details in modal:", error);
        setDetailTask(task);
      } finally {
        setDetailsLoading(false);
      }
    };

    loadTaskDetails();
  }, [show, taskId]);

  if (!show) return null;

  if (detailsLoading && !detailTask) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-40 flex justify-center items-center z-[9999]">
        <div className="bg-white rounded-lg shadow-xl w-[420px] p-6 text-center">
          <div className="w-8 h-8 border-4 border-gray-300 border-t-blue-500 rounded-full animate-spin mx-auto mb-3" />
          <p className="text-sm font-medium text-gray-700">
            Loading task details...
          </p>
        </div>
      </div>
    );
  }

  const hasMapCoords =
    (modalTask?.dropoff_latitude || modalTask?.pickup_latitude) &&
    (modalTask?.dropoff_longitude || modalTask?.pickup_longitude);

  //  LOGS TOGGLE
  const handleToggleLogs = async () => {
    const id = modalTask?.pk_task_id || modalTask?.taskId || modalTask?.id;

    if (!showLogs && onFetchLogs && id) {
      await onFetchLogs(id);
    }

    setShowLogs(!showLogs);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-40 flex justify-center items-center z-[9999]">
      <div className="bg-white rounded-lg shadow-xl max-w-5xl w-full p-4 relative text-sm max-h-[90vh] overflow-y-auto">
        {/* -------- HEADER -------- */}
        <div className="flex justify-between items-center mb-4 pb-3 border-b sticky top-0 bg-white z-10">
          <h2 className="text-lg font-semibold">Delivery Task Details</h2>

          <div className="flex items-center gap-2">
            <select
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value)}
              className="border rounded px-3 py-1 text-xs bg-white"
            >
              {statusOptions.map((st) => (
                <option key={st} value={st}>
                  {st.toUpperCase()}
                </option>
              ))}
            </select>

          <button
  onClick={async () => {
    const currentStatus = modalTask?.status?.toLowerCase();
    const nextStatus = selectedStatus?.toLowerCase();

    if (nextStatus === "unassigned" && currentStatus !== "unassigned") {
      onUnassignRequest?.(modalTask);
      return;
    }

    await onStatusChange?.(
      modalTask?.pk_task_id || modalTask?.taskId || modalTask?.id,
      selectedStatus,
    );
  }}
  className="bg-blue-500 text-white px-3 py-1 rounded text-xs"
>
  {selectedStatus === "unassigned" &&
  modalTask?.status?.toLowerCase() !== "unassigned"
    ? "Assign Driver"
    : "Change Status"}
</button>

            <button
              title="Close"
              className="flex items-center justify-center text-gray-800 w-8 h-8 rounded-full shadow hover:bg-gray-400"
              onClick={onClose}
            >
              <X size={16} />
            </button>
          </div>
        </div>

        {/* -------- MAP + INFO TABLE -------- */}
        <div className="flex flex-col lg:flex-row gap-4">
          {hasMapCoords && (
            <div className="w-full lg:w-1/2 h-74 rounded overflow-hidden border">
              <MapContainer
                center={[
                  modalTask?.dropoff_latitude || modalTask?.pickup_latitude,
                  modalTask?.dropoff_longitude || modalTask?.pickup_longitude,
                ]}
                zoom={14}
                scrollWheelZoom={false}
                style={{ height: "100%", width: "100%" }}
              >
                <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />

                {modalTask?.pickup_latitude && modalTask?.pickup_longitude && (
                  <Marker
                    position={[
                      modalTask.pickup_latitude,
                      modalTask.pickup_longitude,
                    ]}
                    icon={iconRed}
                  >
                    <Popup>Pickup Location</Popup>
                  </Marker>
                )}

                {modalTask?.dropoff_latitude &&
                  modalTask?.dropoff_longitude && (
                    <Marker
                      position={[
                        modalTask.dropoff_latitude,
                        modalTask.dropoff_longitude,
                      ]}
                      icon={iconYellow}
                    >
                      <Popup>Delivery Location</Popup>
                    </Marker>
                  )}

                {modalTask?.driver_latitude && modalTask?.driver_longitude && (
                  <Marker
                    position={[
                      modalTask.driver_latitude,
                      modalTask.driver_longitude,
                    ]}
                    icon={iconDriverActive}
                  >
                    <Popup>Driver Location</Popup>
                  </Marker>
                )}
              </MapContainer>
            </div>
          )}

          {/* RIGHT SIDE INFO + RATING */}
<div className="w-full lg:w-1/2 flex flex-col gap-3">
  <div className="overflow-x-auto">
    <table className="w-full text-xs border">
      <tbody>
        <Row label="Customer" value={modalTask?.customer_name} />
        <Row label="Contact" value={modalTask?.contact_number} />
        <Row label="Email" value={modalTask?.email_address} />
        <Row label="Driver" value={modalTask?.driver_name} />
        <Row label="Driver Mobile" value={modalTask?.driver_mobile} />
        <Row label="Distance" value={modalTask?.taskDistance} />
        <Row label="ETA" value={modalTask?.taskEstimatedTime} last />
      </tbody>
    </table>
  </div>

  {/* RATING SECTION */}
  <div className="rounded-lg border bg-white p-3 shadow-sm">
    <div className="flex items-center justify-between border-b pb-2 mb-3">
      <h3 className="text-xs font-semibold text-gray-800">
        Customer Rating
      </h3>

      <span className="inline-flex items-center gap-1 rounded-full bg-yellow-100 px-2 py-1 text-[11px] font-semibold text-yellow-700">
        <Star size={12} fill="currentColor" />
        {modalTask?.rating || 0}/5
      </span>
    </div>

    <div className="flex items-start gap-3">
      <div className="flex h-9 w-9 items-center justify-center rounded-full bg-yellow-50 text-yellow-600">
        <Star size={18} fill="currentColor" />
      </div>

      <div className="flex-1">
        <div className="mb-1 flex items-center gap-1">
          {Array.from({ length: 5 }).map((_, index) => {
            const active = index < Number(modalTask?.rating || 0);

            return (
              <Star
                key={index}
                size={14}
                className={active ? "text-yellow-500" : "text-gray-300"}
                fill={active ? "currentColor" : "none"}
              />
            );
          })}
        </div>

        <div className="flex items-start gap-2 rounded-md bg-gray-50 p-2">
          <MessageSquare
            size={14}
            className="mt-0.5 shrink-0 text-gray-400"
          />

          <p className="text-xs leading-5 text-gray-700">
            {modalTask?.rating_comment || "No rating comment available"}
          </p>
        </div>
      </div>
    </div>
  </div>
</div>
        </div>

        {/* ADDRESSES */}
        <div className="mt-4 text-xs">
          <div className="mb-2">
            <strong>Pickup Address:</strong>{" "}
            {modalTask?.pickup_address || "N/A"}
          </div>
          <div>
            <strong>Delivery Address:</strong>{" "}
            {modalTask?.delivery_address || "N/A"}
          </div>
        </div>

        {/* LOGS ACCORDION  */}
        <div className="mt-4">
          <div
            onClick={handleToggleLogs}
            className="px-4 py-2 flex justify-between items-center cursor-pointer bg-gray-100 hover:bg-gray-200"
          >
            <h3 className="text-sm font-semibold">Status & Assignment Logs</h3>
            <span
              className={`transition-transform ${showLogs ? "rotate-180" : ""}`}
            >
              ▼
            </span>
          </div>

          <div
            className={`overflow-hidden transition-all duration-300 ${
              showLogs ? "max-h-[1000px]" : "max-h-0"
            }`}
          >
            <div className="p-3 border-t space-y-6 text-sm">
              {statusChangeLogs.length === 0 && assignmentLogs.length === 0 && (
                <div className="text-gray-500 text-center py-4">
                  No logs available
                </div>
              )}

              {statusChangeLogs.length > 0 && (
                <div className="border rounded p-3">
                  <h3 className="text-xs font-semibold mb-3">
                    Status Change Logs
                  </h3>

                  <div className="flex justify-center gap-6 flex-wrap">
                    {statusChangeLogs.map((log, idx) => {
                      const status = log.status?.toLowerCase();

                      const statusColorMap = {
                        unassigned: "bg-gray-400",
                        acknowledged: "bg-blue-500",
                        arrived: "bg-green-500",
                        successful: "bg-green-700",
                        completed: "bg-green-700",
                        "food is ready": "bg-purple-500",
                        "picked up": "bg-orange-500",
                        assigned: "bg-yellow-500",
                        cancelled: "bg-red-500",
                      };

                      const bgColor = statusColorMap[status] || "bg-blue-500";

                      return (
                        <div key={idx} className="flex flex-col items-center">
                          <div
                            className={`w-9 h-9 flex items-center justify-center rounded-full text-white text-sm font-bold ${bgColor}`}
                          >
                            {idx + 1}
                          </div>

                          <div className="text-[10px] mt-1 text-center font-medium">
                            {log.status?.toUpperCase() || "N/A"}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {assignmentLogs.length > 0 && (
                <div className="border rounded p-3">
                  <h3 className="text-xs font-semibold mb-2">
                    Assignment Logs
                  </h3>
                  <div className="overflow-x-auto">
                    <table className="table-auto w-full text-xs border">
                      <thead className="bg-gray-100">
                        <tr>
                          <th className="p-2 border">Assign Type</th>
                          <th className="p-2 border">Driver ID</th>
                          <th className="p-2 border">Driver Name</th>
                          <th className="p-2 border">Task Status</th>
                          <th className="p-2 border">Status</th>
                          <th className="p-2 border">Assignment Date</th>
                          <th className="p-2 border">Process Date</th>
                        </tr>
                      </thead>
                      <tbody>
                        {assignmentLogs.map((log, idx) => (
                          <tr key={idx}>
                            <td className="p-2 border">
                              {log.assignType || "N/A"}
                            </td>
                            <td className="p-2 border">
                              {log.driverId || "N/A"}
                            </td>
                            <td className="p-2 border">
                              {log.driverName?.trim() || "N/A"}
                            </td>
                            <td className="p-2 border">
                              {log.taskStatus || "N/A"}
                            </td>
                            <td className="p-2 border">
                              {log.status || "N/A"}
                            </td>
                            <td className="p-2 border">
                              {log.assignmentDate || "N/A"}
                            </td>
                            <td className="p-2 border">
                              {log.processDate || "N/A"}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// Helper row component
const Row = ({ label, value, last }) => (
  <tr className={last ? "" : "border-b"}>
    <td className="font-semibold px-2 py-1 bg-gray-100">{label}</td>
    <td className="px-2 py-1">{value || "N/A"}</td>
  </tr>
);